import {
  HStack,
  Tooltip,
  IconButton,
  Box,
  useDisclosure,
  Text,
  Image,
  Icon,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaLink, FaClipboardList, FaListUl, FaHistory } from "react-icons/fa";
import { GiCardExchange, GiCrownedSkull } from "react-icons/gi";
import { RiMenuAddFill } from "react-icons/ri";
import { PersonOutlineOutlined } from "@mui/icons-material";
import Brightness1Icon from "@mui/icons-material/Brightness1";
import TextInputModal from "../../../components/TextInputModal";
import { useOrdersStore } from "../store/useOrdersStore";

import services from "../services";
import useNotification, {
  useGaraazToastWithChildren,
} from "../../../utils/useNotification";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import SplitOrdersButton from "../../../components/SplitOrdersButton";
import { useSplitOrdersButton } from "./useSplitOrdersButton";
import { PartConversionState } from "./GaraazOrderComponentFooter";
import SupplierInventorySearch from "../../../components/SupplierInventorySearch/SupplierInventorySearch";
import { buildPreFacetedPartNumbers } from "../utils/buildPreFacetedPartNumbers";
import { useAssignSupplier } from "../hooks/useAssignSupplier";
import { AllocatedTo } from "./GaraazOrderComponent";
import { useExecuteSupplierBucket } from "../hooks/useExecuteSupplierBucket";
import GaraazInfoDialogue from "../../../components/GaraazInfoDialogue";
import { useGetAllSuppliers } from "../queryHooks";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
  AllEstimatesModal,
  AllPartConfirmationsModal,
} from "../../../components/DeliveryOptions";
import { useOrderLabel } from "./GaraazOrderCard/OrderLabelContext";
import GaraazChoser from "./GaraazChoser";
import { OrderStatus, Roles } from "../enums";
import InvoicePopver from "./InvoicePopover";
import InvoicePopoverTrigger from "../../../components/InvoicePopoverTrigger";
import updateOrderHandlerAllocationTime from "../../../utils/updateOrderHandlerAllocationTime";
import { changeParticipantInConversation } from "../../../utils/conversationUtils";
import { OrderRelationType } from "../store/orderStrategies";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import type { VirtualInvoiceRegenerateTradingParams } from "./RegenerateInvoiceDialog";
import PaymentLinksModal from "./PaymentLinksModal";
import { isEnquiryOrderEligibleForPaymentLinks } from "../utils/enquiryPaymentLinksEligibility";
import type { EstimateSplitMode } from "../services/EstimateAllocatorService/types";
import OrderStatusChangeErrorBox from "./OrderStatusChangeErrorBox";
import { hasGaraazInvoiceUploaded } from "../orderUtils";
import AuditTrailModal from "./AuditTrailModal";

export type GaraazOrderComponentFooterVirtualProps = {
  orderData: any;
  handlePartConversionChangeScreen: () => void;
  isCustomerOrderFreezed: boolean;
  isEnquiryLocked?: boolean;
  garaazRemarks: string;
  setGaraazRemarks: React.Dispatch<React.SetStateAction<string>>;
  supplierCancellationReasons: string;
  setSupplierCancellationReasons: React.Dispatch<React.SetStateAction<string>>;
  customerHoldReasons: string;
  setCustomerHoldReasons: React.Dispatch<React.SetStateAction<string>>;
  promiseTimeBreachReasons: string;
  setPromiseTimeBreachReasons: React.Dispatch<React.SetStateAction<string>>;
  onToggleChildOrders?: (orderId: string) => void;
  refetchAllOrders: () => void;
  reFetchChildOrders: () => void;
  setTransactionType: React.Dispatch<React.SetStateAction<string>>;
  isChildOrder?: boolean;
  orderStatus?: any;
  setOrderStatus?: React.Dispatch<any>;
};

const GaraazOrderComponentFooterVirtual: React.FC<
  GaraazOrderComponentFooterVirtualProps
> = ({
  orderData,
  handlePartConversionChangeScreen,
  isCustomerOrderFreezed,
  isEnquiryLocked = false,
  garaazRemarks,
  setGaraazRemarks,
  supplierCancellationReasons,
  setSupplierCancellationReasons,
  customerHoldReasons,
  setCustomerHoldReasons,
  promiseTimeBreachReasons,
  setPromiseTimeBreachReasons,
  onToggleChildOrders,
  refetchAllOrders,
  reFetchChildOrders,
  setTransactionType,
  isChildOrder = false,
  orderStatus,
  setOrderStatus,
}) => {
  const toast = useNotification();
  const garaazToast = useGaraazToastWithChildren();
  const userDetails = useUserDetailsContext();
  const isModificationBlocked = isCustomerOrderFreezed || isEnquiryLocked;
  const { data: configData } = useGetOrderMediaConfig();
  const ff_allow_consolidated_invoice_generation =
    configData?.ff_allow_consolidated_invoice_generation
      ? JSON.parse(configData?.ff_allow_consolidated_invoice_generation)
      : false;
  const { orderLabel, isEnquiryOrder } = useOrderLabel();
  const { shouldShowSplitOrders, count, handleClick } = useSplitOrdersButton(
    orderData,
    onToggleChildOrders,
  );

  const savedOrderRemarks = useOrdersStore(
    useCallback((state) => state.orderRemarks[orderData._id], [orderData._id]),
  );
  const setSavedOrderRemarks = useOrdersStore(
    useCallback((state) => state.setOrderRemarksWithOrderId, []),
  );
  const setSelectedOrderHandler = useOrdersStore(
    useCallback((store) => store.setSelectedOrderHandler, []),
  );

  const orderHandlers = useOrdersStore((state) => state.orderHandlers);
  const orderHandlerOptions = orderHandlers.map((orderHandler) => {
    return {
      name: orderHandler.user.firstName + " " + orderHandler.user.lastName,
      menuItemIcon: (
        <Brightness1Icon
          sx={{
            color: orderHandler?.isActive ? "green" : "red",
            fontSize: "10px",
          }}
        />
      ),
      ...orderHandler,
    };
  });

  const orderHandlerName =
    (orderData?.orderHandler[0]?.firstName ?? "-") +
    " " +
    (orderData?.orderHandler[0]?.lastName ?? "");

  const {
    isOpen: orderRemarksModelOpen,
    onOpen: openOrdersRemarksModel,
    onClose: closeOrdersRemarksModel,
  } = useDisclosure();

  const saveOrderRemarks = async (
    remarks: string,
    garaazOrderRemarks?: string,
  ) => {
    try {
      if (remarks) {
        await services.setOrderRemarks(orderData._id, {
          userName: `${userDetails.firstName} ${userDetails.lastName}`,
          remarks,
        });
        let newOrderRemarks = [
          ...(savedOrderRemarks || []),
          {
            userName: `${userDetails.firstName} ${userDetails.lastName}`,
            remarks: remarks,
          },
        ];
        setSavedOrderRemarks(orderData._id, newOrderRemarks);
      }
      if (garaazOrderRemarks) {
        setGaraazRemarks(garaazOrderRemarks);
      }
      closeOrdersRemarksModel();
      toast({
        description: `${orderLabel} Remarks Updated Successfully`,
        status: "success",
      });
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || "Something went wrong",
        status: "error",
      });
    }
  };
  const { data: allSuppliers } = useGetAllSuppliers();
  const [filteredSuppliers, setFilteredSuppliers] = useState(allSuppliers);
  const orderItems = useOrdersStore(
    useCallback((state) => state.orderItems[orderData._id], [orderData._id]),
  );
  const [assignedSupplier, setAssignedSupplier] = useState<AllocatedTo[]>(
    orderData?.allocatedTo || [],
  );

  const setOrderMetaInfoWithOrderId = useOrdersStore(
    (state) => state.setOrderMetaInfoWithOrderId,
  );

  const refetchOrders = useCallback(() => {
    refetchAllOrders();
    reFetchChildOrders();
  }, [refetchAllOrders, reFetchChildOrders]);

  const assignSupplierHook = useAssignSupplier({
    orderId: orderData._id,
    orderData,
    orderItems,
    setOrderMetaInfoWithOrderId,
    setAssignedSupplier,
    refetchAllOrders: refetchOrders,
    supplierName: "",
    isCustomerOrderFreezed: isModificationBlocked,
  });

  const executeSupplierBucketHook = useExecuteSupplierBucket({
    orderId: orderData._id,
    orderData,
    orderItems,
    setOrderMetaInfoWithOrderId,
    refetchAllOrders: refetchOrders,
    isCustomerOrderFreezed: isModificationBlocked,
  });

  const {
    isOpen: isOrderDetailsExtractedByPhantomModalOpen,
    onOpen: onOrderDetailsExtractedByPhantomModalOpen,
    onClose: onOrderDetailsExtractedByPhantomModalClose,
  } = useDisclosure();

  const {
    isOpen: isSupplierInventorySearchModalOpen,
    onOpen: onSupplierInventorySearchModalOpen,
    onClose: onSupplierInventorySearchModalClose,
  } = useDisclosure();

  const {
    isOpen: isPaymentLinksModalOpen,
    onOpen: onPaymentLinksModalOpen,
    onClose: onPaymentLinksModalClose,
  } = useDisclosure();

  const {
    isOpen: isAllEstimatesOpen,
    onOpen: openAllEstimatesModal,
    onClose: closeAllEstimatesModal,
  } = useDisclosure();

  const {
    isOpen: isAllPartConfirmationsOpen,
    onOpen: openAllPartConfirmationsModal,
    onClose: closeAllPartConfirmationsModal,
  } = useDisclosure();

  const {
    isOpen: isAuditTrailModalOpen,
    onOpen: openAuditTrailModal,
    onClose: closeAuditTrailModal,
  } = useDisclosure();

  const hasOrderTimeline =
    Array.isArray(orderData?.orderTimeline) &&
    orderData.orderTimeline.length > 0;

  const isPaymentLinksEligible = useMemo(
    () => isEnquiryOrderEligibleForPaymentLinks(orderData),
    [orderData],
  );

  const paymentLinksCount = orderData?.paymentLinks?.length ?? 0;
  const [supplierDiscoveryMode, setSupplierDiscoveryMode] =
    useState<EstimateSplitMode>("assign");

  const getSupplierDiscoveryQuantity = useCallback(
    (item: any) => Number(item?.remainingDecisionQuantity ?? 0),
    [],
  );

  const preFacetedPartNumbers = useMemo(
    () =>
      buildPreFacetedPartNumbers(
        supplierDiscoveryMode,
        {
          orderItems,
          supplierCancelledItems: orderData?.supplierCancelledItems,
          cboItems: orderData?.cboItems,
        },
        getSupplierDiscoveryQuantity,
      ),
    [
      getSupplierDiscoveryQuantity,
      orderItems,
      orderData?.supplierCancelledItems,
      orderData?.cboItems,
      supplierDiscoveryMode,
    ],
  );

  const openSupplierInventorySearch = useCallback(
    (qtyMode: EstimateSplitMode) => {
      setSupplierDiscoveryMode(qtyMode);
      if (
        orderData?.isOrderItemsExtractedByOpenAI ||
        orderData?.isVehicleInfoExtractedByOpenAI
      ) {
        onOrderDetailsExtractedByPhantomModalOpen();
      } else {
        onSupplierInventorySearchModalOpen();
      }
    },
    [
      onOrderDetailsExtractedByPhantomModalOpen,
      onSupplierInventorySearchModalOpen,
      orderData?.isOrderItemsExtractedByOpenAI,
      orderData?.isVehicleInfoExtractedByOpenAI,
    ],
  );
  const hasCboDiscoveryQuantity = useMemo(
    () =>
      (orderData?.cboItems ?? []).some(
        (item: any) =>
          Number(
            item?.remainingDecisionQuantity ?? item?.orderedQuantity ?? 0,
          ) > 0,
      ),
    [orderData?.cboItems],
  );

  useEffect(() => {
    const uniqueBrands: any = new Set(
      orderItems?.map((item: any) => item.brandId),
    );
    const uniqueBrandsArray = Array.from(uniqueBrands);
    if (uniqueBrandsArray.some((item) => !item)) {
      setFilteredSuppliers(allSuppliers);
    } else {
      const filteredSupplierTemp = allSuppliers?.filter((supplier: any) => {
        for (let i = 0; i < uniqueBrandsArray.length; i++) {
          let index = supplier.brands.indexOf(uniqueBrandsArray[i]);
          if (index === -1) {
            return false;
          }
        }
        return true;
      });
      setFilteredSuppliers(filteredSupplierTemp);
    }
  }, [orderItems, orderData, allSuppliers]);
  const partConversionCurrentState = useMemo((): PartConversionState => {
    const partConversionStartTime =
      orderData?.metaInfo?.partConversionStartTime;
    const partConversionEndTime = orderData?.metaInfo?.partConversionEndTime;

    if (partConversionStartTime && partConversionEndTime) {
      return PartConversionState.COMPLETED;
    } else if (partConversionStartTime && !partConversionEndTime) {
      return PartConversionState.IN_PROGRESS;
    } else {
      return PartConversionState.NOT_STARTED;
    }
  }, [
    orderData?.metaInfo?.partConversionStartTime,
    orderData?.metaInfo?.partConversionEndTime,
  ]);

  const isPartConversionCompleted = () => {
    return partConversionCurrentState === PartConversionState.COMPLETED;
  };

  const handleAssignSupplierChange = async (
    item: any,
    supplierParts?: any[],
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    splitSelections?: NormalizedDeliverySelection[],
    cptGivenTime?: string,
    splitData?: EnrichedSupplierForDelivery[],
    estimateNo?: string,
    estimateSplitMode?: EstimateSplitMode,
  ) => {
    if (assignSupplierHook?.assignSupplier) {
      await assignSupplierHook.assignSupplier(
        item,
        item.supplierName,
        supplierParts,
        transactionType,
        discount,
        creditNotePercentage,
        splitSelections,
        cptGivenTime,
        splitData,
        estimateNo,
        estimateSplitMode,
      );
    }
  };

  const handleExecuteSupplierBucket = async (
    supplierBucket: any,
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    splitSelections?: NormalizedDeliverySelection[],
    cptGivenTime?: string,
    splitData?: EnrichedSupplierForDelivery[],
    estimateNo?: string,
    estimateSplitMode?: EstimateSplitMode,
  ) => {
    if (executeSupplierBucketHook?.executeSupplierBucket) {
      await executeSupplierBucketHook.executeSupplierBucket(
        supplierBucket,
        transactionType,
        creditNotePercentage,
        discount,
        splitSelections,
        cptGivenTime,
        splitData,
        estimateNo,
        estimateSplitMode,
      );
    }
  };

  const invoiceInfo = useMemo(
    () => ({
      title: "Garaaz",
      invoiceType: "ORDER" as const,
      invoiceMediaId: orderData?.invoiceInfo?.invoiceMediaId ?? "",
      mrpInvoiceMediaId: orderData?.invoiceInfo?.mrpInvoiceMediaId ?? "",
      invoiceNo: orderData?.invoiceInfo?.invoiceNo ?? "",
      invoiceAmount: orderData?.invoiceInfo?.invoiceAmount ?? "",
      outstanding: orderData?.invoiceInfo?.balanceAmount ?? "",
      invoiceDate: orderData?.invoiceInfo?.invoiceDate ?? "",
      invoiceStatus: orderData?.invoiceInfo?.invoiceStatus ?? "",
      paymentLink: orderData?.invoiceInfo?.paymentLink ?? "",
      payment: orderData?.invoiceInfo?.payment,
      consolidatedInvoiceEligibleOrderNos:
        orderData?.invoiceInfo?.consolidatedInvoiceEligibleOrderNos ?? [],
    }),
    [orderData],
  );

  const handleRegenerateOrderInvoice = useCallback(
    async (tradingParams?: VirtualInvoiceRegenerateTradingParams) => {
      if (!orderData?.billingDetails?.isSingleGaraazInvoiceRequired) {
        toast({
          description:
            "Single invoice generation is only allowed for single invoice required enquiries.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      try {
        if (tradingParams) {
          await services.updateOrder(orderData._id, {
            orderDiscount: tradingParams.orderDiscount,
            creditNotePercentage: tradingParams.creditNotePercentage,
          });
        }
        const result = await services.generateConsolidatedInvoice(
          orderData._id,
        );
        toast({
          description:
            result?.message ??
            "Consolidated invoice generation has been started. It may take a few moments for the invoice to appear.",
          status: "success",
        });
        refetchOrders();
      } catch (error: any) {
        const errorMsg = error?.response?.data?.errorMsg;
        if (Array.isArray(errorMsg)) {
          garaazToast({
            title:
              errorMsg[0]?.title ?? "Could not start invoice regeneration.",
            status: "error",
            render: <OrderStatusChangeErrorBox errorMsg={errorMsg} />,
          });
          throw error;
        }

        const message = errorMsg ?? "Could not start invoice regeneration.";
        toast({
          description: Array.isArray(message) ? message[0]?.title : message,
          status: "error",
        });
        throw error;
      }
    },
    [garaazToast, orderData._id, refetchOrders, toast],
  );

  const handleOrderHandlerChange = async (item: any) => {
    try {
      await services.updateOrder(orderData._id, {
        orderHandler: item.userId,
      });
      await updateOrderHandlerAllocationTime(
        orderData._id,
        setOrderMetaInfoWithOrderId,
      );
      try {
        const currentOrderHandler = orderData.orderHandler?.[0];
        const activeSupplier = orderData.allocatedTo?.find(
          (item: any) => item.isActive === true,
        );

        if (
          !activeSupplier?.supplierId ||
          !orderData?.orderNo ||
          !currentOrderHandler?._id
        ) {
          console.warn("Participant change skipped: missing prerequisites", {
            hasActiveSupplier: !!activeSupplier?.supplierId,
            hasOrderNo: !!orderData?.orderNo,
            hasCurrentHandler: !!currentOrderHandler?._id,
          });
        } else {
          const uniqueNameForSupplierConv = `order:${orderData.orderNo}|supplier:${activeSupplier.supplierId}`;
          const uniqueNameForCustomerConv = `order:${orderData.orderNo}|customer:${orderData.customerId}`;
          await changeParticipantInConversation({
            conversationName: uniqueNameForSupplierConv,
            oldHandlerUserId: currentOrderHandler._id,
            newHandlerUserId: item.userId,
          });

          await changeParticipantInConversation({
            conversationName: uniqueNameForCustomerConv,
            oldHandlerUserId: currentOrderHandler._id,
            newHandlerUserId: item.userId,
          });
        }
      } catch (error) {
        console.warn("Participant change failed:", error);
      }

      setSelectedOrderHandler(
        orderData._id,
        item.user,
        isChildOrder ? OrderRelationType.DESCENDANT : OrderRelationType.ROOT,
        orderData.rootOrderId,
      );
    } catch (error) {
      console.error("Failed to assign order handler:", error);
    }
  };

  return (
    <HStack
      flex={1}
      height={"12%"}
      borderRadius={"0px 0px 8px 8px"}
      borderTop={"1px solid lightgrey"}
      borderBottom={"1px solid lightgrey"}
      style={{
        margin: 0,
      }}
      display={"flex"}
      padding={"0.8rem"}
      py={"1.5rem"}
      alignItems={"center"}
      bgColor={"white"}
    >
      <HStack spacing={3} w="45%">
        {shouldShowSplitOrders && (
          <Box>
            <SplitOrdersButton count={count} onClick={handleClick} />
          </Box>
        )}
        <Box>
          <Tooltip label="Flip View">
            <IconButton
              icon={<GiCardExchange />}
              backgroundColor={"#BBE6E3"}
              color={"black"}
              aria-label={""}
              onClick={handlePartConversionChangeScreen}
            />
          </Tooltip>
        </Box>
        <Box>
          <GaraazChoser
            ButtonIcon={<PersonOutlineOutlined />}
            ButtonToolTipLabel="Assign Order Handler"
            MenuItemOnChange={handleOrderHandlerChange}
            displayKey="name"
            options={orderHandlerOptions}
            showIcon={true}
            MenuButtonProps={{
              isDisabled:
                isModificationBlocked ||
                orderStatus === OrderStatus.READY_TO_DISPATCH ||
                orderStatus === OrderStatus.DELIVERED,
            }}
          />
        </Box>
        <Box>
          <Tooltip label="Open Supplier Discovery for Active Quantity">
            <IconButton
              icon={<GiCrownedSkull />}
              backgroundColor={"#BBE6E3"}
              color={"black"}
              aria-label={""}
              onClick={() => openSupplierInventorySearch("assign")}
              isDisabled={!isPartConversionCompleted() || isModificationBlocked}
            />
          </Tooltip>
        </Box>
        <Box>
          <Tooltip label="Open Supplier Discovery for Supplier Cancelled Quantity">
            <IconButton
              icon={<GiCrownedSkull />}
              backgroundColor={"green.300"}
              color={"black"}
              aria-label={""}
              onClick={() => openSupplierInventorySearch("reassign")}
              isDisabled={!isPartConversionCompleted() || isModificationBlocked}
            />
          </Tooltip>
        </Box>
        {hasCboDiscoveryQuantity && (
          <Box>
            <Tooltip label="Open Supplier Discovery for CBO Quantity">
              <IconButton
                icon={<GiCrownedSkull />}
                backgroundColor={"#ECC94B"}
                color={"black"}
                aria-label={""}
                onClick={() => openSupplierInventorySearch("cbo")}
                isDisabled={
                  !isPartConversionCompleted() || isModificationBlocked
                }
              />
            </Tooltip>
          </Box>
        )}
        <SupplierInventorySearch
          isOpen={isSupplierInventorySearchModalOpen}
          onClose={() => {
            onSupplierInventorySearchModalClose();
          }}
          onSelectionClose={() => {
            onSupplierInventorySearchModalClose();
          }}
          preFacetedPartNumbers={preFacetedPartNumbers}
          customerGeoPoint={orderData?.shipmentAddress?.location}
          handleSelectListItem={handleAssignSupplierChange}
          associations={orderData?.customer?.supplierAssociations}
          orderId={orderData._id}
          orderNumber={orderData?.orderNo}
          setTransactionType={setTransactionType}
          orderData={orderData}
          handleExecuteSupplierBucket={handleExecuteSupplierBucket}
          isSupplierBucketExecuting={executeSupplierBucketHook.isExecuting}
          allSuppliers={allSuppliers}
          filteredSuppliers={filteredSuppliers}
          estimateSplitMode={supplierDiscoveryMode}
        ></SupplierInventorySearch>

        <GaraazInfoDialogue
          isOpen={isOrderDetailsExtractedByPhantomModalOpen}
          onOpen={onOrderDetailsExtractedByPhantomModalOpen}
          onClose={() => {
            onOrderDetailsExtractedByPhantomModalClose();
            onSupplierInventorySearchModalOpen();
          }}
          leftBtnText={`Already Reviewed`}
          submitBtnText={`Review`}
          onSubmitBtn={onOrderDetailsExtractedByPhantomModalClose}
          alertBodyText={`This ${orderLabel} might contain some order items or vehicle related information extracted by Phantom. Please review before proceeding.`}
          alertGenericHeaderText={
            <Text fontSize={"xl"} display={"flex"} alignItems={"center"}>
              Phantom Alert !
              <Image
                width={30}
                src="https://garaaz-store.s3.ap-south-1.amazonaws.com/7a6e6ff21a3b4239bf82d94d38a7e5ef.png"
              ></Image>
            </Text>
          }
        ></GaraazInfoDialogue>
        <Box>
          <Tooltip label={`Add ${orderLabel} Remarks`}>
            <IconButton
              icon={<RiMenuAddFill />}
              backgroundColor={"#BBE6E3"}
              color={"black"}
              aria-label={""}
              onClick={openOrdersRemarksModel}
              isDisabled={isModificationBlocked}
            />
          </Tooltip>
          <TextInputModal
            modalHeader={`${orderLabel} Remarks`}
            inputPlaceHolder={`Add ${orderLabel} Remarks`}
            isOpen={orderRemarksModelOpen}
            onSave={saveOrderRemarks}
            onClose={closeOrdersRemarksModel}
            displaySecondaryInput={true}
            secondaryInput={garaazRemarks || ""}
            modalSecondaryHeader="Garaaz Remarks"
            secondaryInputPlaceHolder="Add Garaaz Remarks"
            displayThirdInput={true}
            modalThirdHeader="Supplier Cancellation Reasons"
            thirdInputPlaceHolder="Add Supplier Cancellation Reason"
            thirdInput={supplierCancellationReasons || ""}
            displayFourthInput={true}
            modalFourthHeader="Customer Hold Reasons"
            fourthInputPlaceHolder="Add Customer Hold Reason"
            fourthInput={customerHoldReasons || ""}
            displayFifthInput={true}
            modalFifthHeader="Promise Time Breach Reasons"
            fifthInputPlaceHolder="Add Promise Time Breach Reason"
            fifthInput={promiseTimeBreachReasons || ""}
          />
        </Box>
        {isPaymentLinksEligible && (
          <Box>
            <Tooltip label="Payment Links">
              <IconButton
                icon={<FaLink />}
                backgroundColor={"#BBE6E3"}
                color={"black"}
                aria-label={"Payment Links"}
                onClick={onPaymentLinksModalOpen}
              />
            </Tooltip>
            <PaymentLinksModal
              isOpen={isPaymentLinksModalOpen}
              onClose={onPaymentLinksModalClose}
              orderData={orderData}
              onPaymentLinkCreatedForEnquiry={() => {
                refetchOrders();
              }}
            />
          </Box>
        )}
        {hasOrderTimeline && (
          <Box>
            <Tooltip label="Audit Trail">
              <IconButton
                icon={<FaHistory />}
                backgroundColor={"#BBE6E3"}
                color={"black"}
                aria-label="Audit Trail"
                onClick={openAuditTrailModal}
                isDisabled={orderData.isDeleted}
              />
            </Tooltip>
            <AuditTrailModal
              isOpen={isAuditTrailModalOpen}
              onClose={closeAuditTrailModal}
              orderTimeline={orderData?.orderTimeline}
            />
          </Box>
        )}
        {isEnquiryOrder && (
          <Box>
            <Tooltip label="All Estimates">
              <IconButton
                icon={<Icon as={FaClipboardList as any} boxSize={5} />}
                backgroundColor={"#BBE6E3"}
                color={"black"}
                aria-label={"All Estimates"}
                onClick={openAllEstimatesModal}
                isDisabled={orderData.isDeleted}
              />
            </Tooltip>
            <AllEstimatesModal
              isOpen={isAllEstimatesOpen}
              onClose={closeAllEstimatesModal}
              orderNo={orderData.orderNo}
              orderData={orderData}
            />
          </Box>
        )}
        {isEnquiryOrder && (
          <Box>
            <Tooltip label="All Part Confirmation Requests">
              <IconButton
                icon={<Icon as={FaListUl as any} boxSize={5} />}
                backgroundColor={"#BBE6E3"}
                color={"black"}
                aria-label={"All Part Confirmation Requests"}
                onClick={openAllPartConfirmationsModal}
                isDisabled={orderData.isDeleted}
              />
            </Tooltip>
            <AllPartConfirmationsModal
              isOpen={isAllPartConfirmationsOpen}
              onClose={closeAllPartConfirmationsModal}
              orderNo={orderData.orderNo}
            />
          </Box>
        )}
      </HStack>
      <Box
        display={"flex"}
        flexWrap="wrap"
        gap={5}
        style={{ marginLeft: "auto", alignItems: "start" }}
        paddingTop={5}
      >
        {(ff_allow_consolidated_invoice_generation === "true" ||
          ff_allow_consolidated_invoice_generation === true) &&
          (invoiceInfo?.invoiceNo ||
            (orderData?.creditNotes?.length ?? 0) > 0 ||
            (orderData?.voidedInvoices?.length ?? 0) > 0 ||
            userDetails.userSubType === Roles.HEAD_OF_OPERATIONS) && (
            <InvoicePopver
              copierKeys={["invoiceNo", "consolidatedInvoiceEligibleOrderNos"]}
              discountPercentage={orderData?.discount}
              creditNotePercentage={orderData?.creditNotePercentage}
              Invoices={[invoiceInfo]}
              Challans={[]}
              CreditNotes={orderData?.creditNotes ?? []}
              voidedInvoices={orderData?.voidedInvoices ?? []}
              canVoidInvoice={
                userDetails.userSubType === Roles.HEAD_OF_OPERATIONS
              }
              canRegenerateOrderInvoice={
                userDetails.userSubType === Roles.HEAD_OF_OPERATIONS &&
                !hasGaraazInvoiceUploaded(orderData)
              }
              canRegenerateDeliveryInvoice={false}
              hasOrderInvoice={hasGaraazInvoiceUploaded(orderData)}
              hasDeliveryInvoice={false}
              onRegenerateOrderInvoice={handleRegenerateOrderInvoice}
              orderStatus={orderStatus}
              orderNo={orderData.orderNo}
              onVoidSuccess={refetchOrders}
              isVirtualInvoiceRegenerationMode
              isSingleInvoiceRequired={
                orderData?.billingDetails?.isSingleGaraazInvoiceRequired
              }
            >
              <InvoicePopoverTrigger
                invoiceStatus={invoiceInfo.invoiceStatus}
              ></InvoicePopoverTrigger>
            </InvoicePopver>
          )}

        <Box ml={5}>
          <Text fontWeight={"bold"} color={"#1A202C"}>
            HANDLER
          </Text>
          <Text
            color="teal.600"
            textTransform={"uppercase"}
            fontWeight={"semibold"}
            fontSize={"x-small"}
          >
            {`${orderHandlerName}`}
          </Text>
        </Box>
      </Box>
    </HStack>
  );
};

export default GaraazOrderComponentFooterVirtual;
